import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import WalkInEnquiryDialog from '@/components/WalkInEnquiryDialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CalendarBlank, ArrowRight, UserPlus, Phone, WhatsappLogo, CheckCircle } from '@phosphor-icons/react';

const PALETTE = ['#E04B2A', '#2B6EF0', '#E5A100', '#10A882', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];
const fmt = (n) => {
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n?.toLocaleString('en-IN') || '0';
};
const fmtINR = (n) => `Rs.${(n || 0).toLocaleString('en-IN')}`;

// Mirrors LeadListPage.js's STAGE_ACCENT (not exported there) so lead-stage colours
// stay consistent across the leads table and this dashboard's work-queue rows.
const STAGE_ACCENT = {
  new_inquiry: '#2B6EF0', contacted: '#06B6D4', qualified: '#8B5CF6',
  demo_scheduled: '#E5A100', demo_attended: '#F97316', application: '#6366F1',
  fee_pending: '#EAB308', enrolled: '#10A882', lost: '#EF4444', on_hold: '#808080',
};

function greetingWord() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// next_follow_up is stored as a UTC ISO-8601 string (see backend _normalize_follow_up).
function daysOverdue(nextFollowUp) {
  const d = new Date(`${nextFollowUp.slice(0, 10)}T00:00:00Z`);
  const today = new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.max(1, Math.round((today - d) / 86400000));
}

// KPI tile shell shared by both the "This Month" pair and the "Live Balance" card.
function KpiTile({ label, value, subtext, accent, loading, testId, hint }) {
  return (
    <Card className="border border-border rounded-xl shadow-none overflow-hidden" data-testid={testId}>
      <CardContent className="p-4 relative">
        <div className="absolute top-0 left-0 w-full h-[3px]" style={{ backgroundColor: accent }} />
        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.08em] font-semibold mt-1">
          {label}
          {hint && <span title={hint} className="ml-1 cursor-help text-muted-foreground/70 no-underline border-b border-dotted border-muted-foreground/40">&#9432;</span>}
        </p>
        {loading ? (
          <div className="h-7 w-24 bg-secondary rounded-md animate-pulse mt-2" />
        ) : (
          <p className="font-display text-2xl font-extrabold text-foreground mt-1">{value}</p>
        )}
        <p className="text-[10px] text-muted-foreground mt-0.5">{subtext}</p>
      </CardContent>
    </Card>
  );
}

// The three corrected KPI cards (ADM-11): "This Month" figures grouped together,
// "Live Balance" (all-time outstanding) visually separated since it's a different
// kind of number — it doesn't reset and isn't scoped to the period above it.
function KpiCardsTop({ data, loading }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="kpi-cards-top">
      <div className="sm:col-span-2">
        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.14em] font-bold mb-2">This Month</p>
        <div className="grid grid-cols-2 gap-3">
          <KpiTile
            label="Enrolled This Month"
            value={data?.enrolled_this_month ?? 0}
            subtext="students &middot; this month"
            accent="#10A882"
            loading={loading}
            testId="kpi-enrolled-month"
          />
          <KpiTile
            label="Collected This Month"
            value={fmtINR(data?.collected_this_month)}
            subtext="received this month"
            accent="#2B6EF0"
            loading={loading}
            testId="kpi-collected-month"
          />
        </div>
      </div>
      <div className="sm:pl-4 sm:border-l sm:border-border">
        <p className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2" style={{ color: '#EF4444' }}>Live Balance</p>
        <KpiTile
          label="Pending Fees"
          value={fmtINR(data?.pending_fees_all_time)}
          subtext="outstanding &middot; all time"
          accent="#EF4444"
          loading={loading}
          testId="kpi-pending-fees"
          hint="Includes balances carried forward from previous months, not just this month's dues"
        />
      </div>
    </div>
  );
}

// ADM-18: the five hybrid dashboard statuses (display labels over the 10 DB stages).
const WORK_CHIPS = [
  { key: 'work', label: 'Work', accent: '#E04B2A' },
  { key: 'new', label: 'New', accent: '#2B6EF0' },
  { key: 'in_progress', label: 'In Progress', accent: '#E5A100' },
  { key: 'enrolled', label: 'Enrolled', accent: '#10A882' },
  { key: 'lost', label: 'Lost', accent: '#EF4444' },
];

const EMPTY_COPY = {
  work: "No actions pending — you're all caught up. ☕",
  new: 'No new inquiries waiting.',
  in_progress: 'Nothing in progress right now.',
  enrolled: 'No enrolled students yet.',
  lost: 'No lost leads.',
};

// One row in the single lead list. In the "Work" bucket it flags follow-up
// urgency (overdue / due today) or a brand-new inquiry; other buckets show the
// stage + last-touch date.
// Digits-only phone, WhatsApp defaulting to the student phone if no separate
// WhatsApp number was captured; both quick actions are no-ops without a number.
function digitsOnly(v) { return (v || '').replace(/\D/g, ''); }

function LeadRow({ lead, bucket, navigate, onMarkContacted }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const fu = lead.next_follow_up ? lead.next_follow_up.slice(0, 10) : null;
  const isOverdue = fu && fu < todayStr;
  const isDueToday = fu && fu === todayStr;
  const isNew = lead.stage === 'new_inquiry';

  let badge = null;
  if (bucket === 'work') {
    if (isOverdue) badge = { text: 'Overdue', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', note: `${daysOverdue(lead.next_follow_up)} day${daysOverdue(lead.next_follow_up) === 1 ? '' : 's'} overdue` };
    else if (isDueToday) badge = { text: 'Due Today', color: '#E5A100', bg: 'rgba(229,161,0,0.12)', note: 'Due today' };
    else if (isNew) badge = { text: 'New', color: '#2B6EF0', bg: 'rgba(43,110,240,0.1)' };
  }

  const phone = digitsOnly(lead.student_phone);
  const waPhone = digitsOnly(lead.whatsapp) || phone;
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5 px-2 -mx-2 border-b border-border last:border-0 cursor-pointer transition-colors rounded-md hover:bg-secondary/40"
      style={{ backgroundColor: isOverdue && bucket === 'work' ? 'rgba(239,68,68,0.05)' : 'transparent' }}
      onClick={() => navigate(`/leads/${lead.id}`)}
      data-testid={`wq-row-${lead.id}`}
    >
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <span className="w-1.5 h-1.5 rounded-sm shrink-0" style={{ backgroundColor: STAGE_ACCENT[lead.stage] || '#808080' }} />
        <span className="text-[12px] font-semibold text-foreground truncate">{lead.student_name}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold shrink-0 hidden sm:inline">{lead.stage?.replace(/_/g, ' ')}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {badge?.note && <span className="text-[10px] text-muted-foreground font-mono hidden md:inline mr-0.5">{badge.note}</span>}
        {badge && (
          <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mr-1" style={{ color: badge.color, backgroundColor: badge.bg }}>{badge.text}</span>
        )}
        {!badge && (
          <span className="text-[10px] text-muted-foreground font-mono mr-1 hidden sm:inline">
            {(lead.last_contacted || lead.updated_at) ? new Date(lead.last_contacted || lead.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
          </span>
        )}
        {phone && (
          <a href={`tel:${phone}`} onClick={stop} title="Call" data-testid={`row-call-${lead.id}`}
             className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors">
            <Phone size={12} weight="bold" />
          </a>
        )}
        {waPhone && (
          <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noreferrer" onClick={stop} title="WhatsApp" data-testid={`row-whatsapp-${lead.id}`}
             className="inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors"
             style={{ color: '#25D366', borderColor: '#25D36640' }}>
            <WhatsappLogo size={12} weight="bold" />
          </a>
        )}
        {isNew && onMarkContacted && (
          <button
            onClick={(e) => { stop(e); onMarkContacted(lead); }}
            title="Mark Contacted"
            data-testid={`row-mark-contacted-${lead.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 h-7 rounded-md border transition-colors"
            style={{ color: '#10A882', borderColor: '#10A88240' }}
          >
            <CheckCircle size={12} weight="bold" /> <span className="hidden md:inline">Contacted</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ADM-18: counselor dashboard = a status filter-bar over one lead list, defaulting
// to "Work" (everything the counselor has an action on). Replaces the analytics
// dashboard for role === 'counselor'.
function CounselorWorkQueue({ user, kpiData, kpiLoading }) {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState('work');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWalkIn, setShowWalkIn] = useState(false);

  const fetchBucket = useCallback((b) => {
    setLoading(true);
    api.get(`/api/leads/work-queue?bucket=${b}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchBucket(bucket); }, [bucket, fetchBucket]);

  const handleWalkInCreated = () => {
    setShowWalkIn(false);
    toast.success('Walk-in enquiry logged');
    fetchBucket(bucket);
  };

  const handleMarkContacted = async (lead) => {
    try {
      await api.put(`/api/leads/${lead.id}/stage`, { stage: 'contacted' });
      toast.success(`${lead.student_name} marked Contacted`);
      fetchBucket(bucket);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const counts = data?.counts || {};
  const leads = data?.leads || [];
  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const workCount = counts.work || 0;
  const activeChip = WORK_CHIPS.find(c => c.key === bucket);

  return (
    <div data-testid="counselor-work-queue" className="stagger-in">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">
            {greetingWord()}{firstName ? `, ${firstName}` : ''}.
          </h1>
          <div className="mt-2">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
              style={{
                color: workCount > 0 ? '#E04B2A' : '#10A882',
                backgroundColor: workCount > 0 ? 'rgba(224,75,42,0.08)' : 'rgba(16,168,130,0.08)',
              }}
              data-testid="workqueue-chip"
            >
              {workCount > 0 ? `${workCount} to work on` : 'All caught up'}
            </span>
          </div>
        </div>
        <Button
          data-testid="dashboard-create-walkin-btn"
          onClick={() => setShowWalkIn(true)}
          className="h-9 rounded-lg text-xs gap-1.5 font-semibold text-white shrink-0"
          style={{ backgroundColor: '#E04B2A' }}
        >
          <UserPlus size={14} weight="bold" /> New Walk-in Enquiry
        </Button>
        <WalkInEnquiryDialog open={showWalkIn} onOpenChange={setShowWalkIn} onCreated={handleWalkInCreated} />
      </div>

      <KpiCardsTop data={kpiData} loading={kpiLoading} />

      {/* Status filter bar */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="status-filter-bar">
        {WORK_CHIPS.map(chip => {
          const active = bucket === chip.key;
          const count = counts[chip.key] ?? 0;
          return (
            <button
              key={chip.key}
              onClick={() => setBucket(chip.key)}
              data-testid={`chip-${chip.key}`}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-colors"
              style={{
                color: active ? '#fff' : chip.accent,
                backgroundColor: active ? chip.accent : 'transparent',
                borderColor: active ? chip.accent : 'var(--border)',
              }}
            >
              {chip.label}
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold"
                style={{
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  backgroundColor: active ? 'rgba(255,255,255,0.22)' : 'var(--secondary)',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Single lead list for the selected status */}
      <Card className="border border-border rounded-xl shadow-none mb-6" data-testid="wq-list-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-foreground">
              {activeChip?.label}
              {!loading && <span className="text-muted-foreground font-normal ml-1.5">({leads.length})</span>}
            </h3>
            <button onClick={() => navigate('/leads')} className="text-[11px] font-semibold text-[#2B6EF0] hover:underline flex items-center gap-1" data-testid="view-all-leads-link">
              All leads <ArrowRight size={11} weight="bold" />
            </button>
          </div>
          {loading ? (
            <p className="text-xs text-muted-foreground font-mono py-6 text-center">loading...</p>
          ) : leads.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">{EMPTY_COPY[bucket]}</p>
          ) : (
            <div className="space-y-0">
              {leads.map(lead => <LeadRow key={lead.id} lead={lead} bucket={bucket} navigate={navigate} onMarkContacted={handleMarkContacted} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    api.get('/api/analytics/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/api/calendar/upcoming').then(r => setUpcomingEvents((r.data.events || []).slice(0, 5))).catch(console.error);
  }, []);

  // ADM-10: counselors get a work-queue triage view instead of the org-wide analytics
  // dashboard below. KPI data (fetched above) is shared so there's no duplicate call.
  if (user?.role === 'counselor') {
    return <CounselorWorkQueue user={user} kpiData={data} kpiLoading={loading} />;
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground font-mono">loading...</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Failed to load</div>;

  const tt = {
    fontSize: 11,
    borderRadius: 8,
    fontFamily: 'DM Sans',
    background: theme === 'dark' ? '#1C1C1C' : '#fff',
    border: `1px solid ${theme === 'dark' ? '#2E2E2E' : '#E3E0DB'}`,
    color: theme === 'dark' ? '#EDEBE6' : '#191919',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  };
  const ax = theme === 'dark' ? '#666' : '#999';

  const kpis = [
    { label: 'Total Leads', value: data.total_leads, accent: '#2B6EF0' },
    { label: 'Qualified', value: data.qualified_leads, accent: '#8B5CF6' },
    { label: 'Enrolled', value: data.enrolled_students, accent: '#10A882' },
    { label: 'Conversion', value: `${data.conversion_rate}%`, accent: '#10A882' },
    { label: 'Revenue', value: `Rs.${fmt(data.revenue_collected)}`, accent: '#E5A100' },
    { label: 'Outstanding', value: `Rs.${fmt(data.outstanding_dues)}`, accent: '#E04B2A' },
  ];

  return (
    <div data-testid="dashboard-page" className="stagger-in">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">YuHasPro Group &mdash; overview</p>
      </div>

      {/* ADM-11: corrected live-balance vs this-month KPI cards */}
      <KpiCardsTop data={data} loading={loading} />

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="kpi-cards">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border border-border rounded-xl shadow-none hover:shadow-md transition-shadow card-lift overflow-hidden">
            <CardContent className="p-4 relative">
              <div className="absolute top-0 left-0 w-full h-[3px] rounded-t-xl" style={{ backgroundColor: kpi.accent }} />
              <p className="font-display text-2xl font-extrabold text-foreground mt-1">{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-[0.08em] font-semibold mt-1">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        <Card className="lg:col-span-3 border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-5">Leads by Source</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.leads_by_source} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
                <XAxis dataKey="source" tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 9, fill: ax, fontFamily: 'JetBrains Mono' }} />
                <Tooltip contentStyle={tt} />
                <Bar dataKey="count" fill="#E04B2A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-5">Pipeline</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.leads_by_stage} dataKey="count" nameKey="stage" cx="50%" cy="50%" outerRadius={72} innerRadius={42} paddingAngle={2} strokeWidth={0}>
                  {data.leads_by_stage.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tt} formatter={(v, n) => [v, n.replace('_', ' ')]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {data.leads_by_stage.map((s, i) => (
                <span key={s.stage} className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono">
                  <span className="w-2 h-2 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                  {s.stage?.replace('_', ' ')} ({s.count})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card className="border border-border rounded-xl shadow-none mb-6" data-testid="upcoming-events-card">
        <CardContent className="p-5">
          <h3 className="font-display font-bold text-sm text-foreground mb-4 flex items-center gap-2">
            <CalendarBlank size={16} weight="bold" /> Upcoming Events
          </h3>
          <div className="space-y-0">
            {upcomingEvents.map((evt) => (
              <div key={evt._id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#E5A10015', color: '#E5A100' }}>
                  <CalendarBlank size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-foreground font-medium truncate">{evt.summary}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                    {evt.start_time ? new Date(evt.start_time).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    {evt.type ? ` \u00b7 ${evt.type.replace('_', ' ')}` : ''}
                    {evt.lead_id ? ` \u00b7 Lead` : ''}
                  </p>
                </div>
                {evt.google_link && (
                  <a href={evt.google_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline font-mono shrink-0">
                    Open
                  </a>
                )}
              </div>
            ))}
            {upcomingEvents.length === 0 && (
              <p className="text-xs text-muted-foreground py-4 text-center font-mono">No upcoming events this week</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Counselor Workload */}
      {data.counselor_performance?.length > 0 && (
        <Card className="border border-border rounded-xl shadow-none mb-6" data-testid="counselor-workload-card">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Counselor Workload</h3>
            <div className="grid grid-cols-4 gap-2 mb-3 text-[9px] text-muted-foreground uppercase tracking-wider font-bold px-1">
              <span>Counselor</span>
              <span className="text-center">Active Leads</span>
              <span className="text-center">Enrolled</span>
              <span className="text-center">Conversion</span>
            </div>
            <div className="space-y-0">
              {(() => {
                const maxLeads = Math.max(...data.counselor_performance.map(c => c.total_leads || 0), 1);
                return data.counselor_performance.slice(0, 8).map((c) => {
                  const pct = ((c.total_leads || 0) / maxLeads) * 100;
                  const barColor = pct > 80 ? '#EF4444' : pct > 50 ? '#E5A100' : '#10A882';
                  return (
                    <div key={c.counselor_id} className="py-2.5 border-b border-border last:border-0">
                      <div className="grid grid-cols-4 gap-2 items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground font-display shrink-0">
                            {c.name?.charAt(0)}
                          </div>
                          <span className="text-[12px] text-foreground font-medium truncate">{c.name}</span>
                        </div>
                        <span className="text-[12px] text-foreground font-mono text-center font-semibold">{c.total_leads}</span>
                        <span className="text-[12px] font-mono text-center font-semibold" style={{ color: '#10A882' }}>{c.enrolled}</span>
                        <span className="text-[12px] text-foreground font-mono text-center font-semibold">{c.conversion_rate}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-sm bg-secondary overflow-hidden mx-1">
                        <div className="h-full rounded-sm transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Counselors + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Counselor Performance</h3>
            <div className="space-y-0">
              {data.counselor_performance?.slice(0, 6).map((c, i) => (
                <div key={c.counselor_id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[10px] text-muted-foreground font-mono w-4">{i + 1}</span>
                    <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center text-[10px] font-bold text-muted-foreground font-display">
                      {c.name?.charAt(0)}
                    </div>
                    <span className="text-[13px] text-foreground font-medium">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span className="text-muted-foreground">{c.total_leads}</span>
                    <span style={{ color: '#10A882' }}>{c.enrolled}</span>
                    <span className="text-foreground font-semibold">{c.conversion_rate}%</span>
                  </div>
                </div>
              ))}
              {(!data.counselor_performance || data.counselor_performance.length === 0) && (
                <p className="text-xs text-muted-foreground py-4 text-center">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border rounded-xl shadow-none">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-sm text-foreground mb-4">Recent Activity</h3>
            <div className="space-y-0">
              {data.recent_activities?.slice(0, 8).map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0`} style={{
                    backgroundColor: a.type === 'stage_change' ? '#2B6EF0' : a.type === 'call' ? '#10A882' : a.type === 'note' ? '#808080' : '#8B5CF6'
                  }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] text-foreground truncate">{a.content}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {a.user_name} &middot; {a.type?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              ))}
              {(!data.recent_activities || data.recent_activities.length === 0) && (
                <p className="text-xs text-muted-foreground py-4 text-center">No activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
